import {register} from '../vendor/theme-scripts/theme-sections';
import * as a11y from '../vendor/theme-scripts/theme-a11y';
import {slider} from '../features/slider';
import parallaxHero from '../features/parallax-hero';
import {CollectionFiltersForm} from '../features/collection-filters-form';
import {tooltipSection} from '../features/tooltip';
import {RangeSlider} from '../features/range-slider';
import {isMobile} from '../util/media-query';

const selectors = {
  dataSort: 'data-sort-enabled',
  sortLinks: '[data-sort-link]',
  sortValue: 'data-value',
  sortButton: '[data-popout-toggle]',
  sortButtonText: '[data-sort-button-text]',
  collectionSidebar: '[data-collection-sidebar]',
  collectionSidebarSlideOut: '[data-collection-sidebar-slide-out]',
  collectionSidebarCloseButton: '[data-collection-sidebar-close]',
  groupTagsButton: '[data-aria-toggle]',
  collectionNav: '[data-collection-nav]',
  animation: '[data-animation]',
};

const classes = {
  animated: 'drawer--animated',
  hiding: 'is-hiding',
  expanded: 'expanded',
  noMobileAnimation: 'no-mobile-animation',
  active: 'is-active',
  focused: 'is-focused',
};

let sections = {};
class Collection {
  constructor(section) {
    this.container = section.container;
    this.sort = this.container.querySelector(`[${selectors.dataSort}]`);
    this.sortButton = this.container.querySelector(selectors.sortButton);
    this.sortLinks = this.container.querySelectorAll(selectors.sortLinks);
    this.collectionSidebar = this.container.querySelector(selectors.collectionSidebar);
    this.collectionSidebarCloseButtons = this.container.querySelectorAll(selectors.collectionSidebarCloseButton);
    this.groupTagsButton = this.container.querySelector(selectors.groupTagsButton);
    this.collectionNav = this.container.querySelector(selectors.collectionNav);
    this.accessibility = a11y;

    this.groupTagsButtonClickEvent = (evt) => this.groupTagsButtonClick(evt);
    this.collectionSidebarCloseEvent = (evt) => this.collectionSidebarClose(evt);
    this.onSortButtonClickEvent = (e) => this.onSortButtonClick(e);
    this.onSortCheckEvent = (e) => this.onSortCheck(e);
    this.sidebarResizeEvent = () => this.toggleSidebarSlider();

    this.init();
  }

  init() {
    if (this.sort) {
      this.initSort();
    }

    if (this.groupTagsButton !== null) {
      document.addEventListener('theme:resize:width', this.sidebarResizeEvent);

      this.groupTagsButton.addEventListener('click', this.groupTagsButtonClickEvent);

      // Prevent filters closing animation on page load
      if (this.collectionSidebar) {
        setTimeout(() => {
          this.collectionSidebar.classList.remove(classes.noMobileAnimation);
        }, 1000);
      }

      const toggleFiltersObserver = new MutationObserver((mutationList) => {
        for (const mutation of mutationList) {
          if (mutation.type === 'attributes') {
            const expanded = mutation.target.getAttribute('aria-expanded') == 'true';

            if (expanded) {
              this.showSidebarCallback();
            }
          }
        }
      });

      toggleFiltersObserver.observe(this.groupTagsButton, {
        attributes: true,
        childList: false,
        subtree: false,
      });
    }

    if (this.collectionSidebarCloseButtons.length) {
      this.collectionSidebarCloseButtons.forEach((button) => {
        button.addEventListener('click', this.collectionSidebarCloseEvent);
      });
    }

    // Hide filters sidebar on ESC keypress
    this.container.addEventListener(
      'keyup',
      function (evt) {
        if (evt.code !== 'Escape') {
          return;
        }
        this.hideSidebar();
      }.bind(this)
    );

    if (this.collectionSidebar) {
      this.collectionSidebar.addEventListener('transitionend', () => {
        if (!this.collectionSidebar.classList.contains(classes.expanded)) {
          this.collectionSidebar.classList.remove(classes.animated);
        }
      });

      this.toggleSidebarSlider();

      this.collectionSidebar.addEventListener('theme:filter:update', () => {
        const sidebarCloseButtons = this.container.querySelectorAll(selectors.collectionSidebarCloseButton);

        if (sidebarCloseButtons.length) {
          sidebarCloseButtons.forEach((button) => {
            button.addEventListener('click', this.collectionSidebarCloseEvent);
          });
        }
      });
    }
  }

  sortActions(link, submitForm = true) {
    const sort = link ? link.getAttribute(selectors.sortValue) : '';
    this.sort.setAttribute(selectors.dataSort, sort);

    const sortButtonText = this.sort.querySelector(selectors.sortButtonText);
    const sortActive = this.sort.querySelector(`.${classes.active}`);
    if (sortButtonText) {
      const linkText = link ? link.textContent.trim() : '';
      sortButtonText.textContent = linkText;
    }
    if (sortActive) {
      sortActive.classList.remove(classes.active);
    }
    this.sort.classList.toggle(classes.active, link);

    if (link) {
      link.parentElement.classList.add(classes.active);

      if (submitForm) {
        link.dispatchEvent(
          new CustomEvent('theme:filter:sort-update', {
            bubbles: true,
            detail: {
              href: sort,
            },
          })
        );
      }
    }
  }

  onSortButtonClick(e) {
    e.preventDefault();

    if (this.sortButton) {
      this.sortButton.dispatchEvent(new Event('click'));
    }
    this.sortActions(e.currentTarget);
  }

  onSortCheck(e) {
    let link = null;
    if (window.location.search.includes('sort_by')) {
      const url = new window.URL(window.location.href);
      const urlParams = url.searchParams;

      for (const [key, val] of urlParams.entries()) {
        const linkSort = this.sort.querySelector(`[${selectors.sortValue}="${val}"]`);
        if (key.includes('sort_by') && linkSort) {
          link = linkSort;
          break;
        }
      }
    }

    this.sortActions(link, false);
  }

  initSort() {
    this.sortLinks.forEach((link) => {
      link.addEventListener('click', this.onSortButtonClickEvent);
    });
    this.sort.addEventListener('theme:filter:sort', this.onSortCheckEvent);

    if (this.sortButton) {
      this.sortButton.addEventListener('click', () => {
        const isFiltersSidebarOpen = this.collectionSidebar.classList.contains(classes.expanded);

        if (isMobile() && isFiltersSidebarOpen) {
          this.hideSidebar();
        }
      });
    }
  }

  showSidebarCallback() {
    const collectionSidebarSlideOut = this.container.querySelector(selectors.collectionSidebarSlideOut);
    const isScrollLocked = document.documentElement.hasAttribute('data-scroll-locked');

    const isMobileView = isMobile();
    this.collectionSidebar.classList.add(classes.animated);

    if (collectionSidebarSlideOut === null) {
      if (!isMobileView && isScrollLocked) {
        this.accessibility.removeTrapFocus();
        document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true}));
      }
    }

    if (isMobileView || collectionSidebarSlideOut !== null) {
      if (collectionSidebarSlideOut) {
        this.accessibility.trapFocus(this.collectionSidebar, {
          elementToFocus: this.collectionSidebar.querySelector(selectors.collectionSidebarCloseButton),
        });
      }
      document.dispatchEvent(new CustomEvent('theme:scroll:lock', {bubbles: true}));
    }
  }

  hideSidebar() {
    const collectionSidebarSlideOut = this.container.querySelector(selectors.collectionSidebarSlideOut);
    const isScrollLocked = document.documentElement.hasAttribute('data-scroll-locked');

    this.groupTagsButton.setAttribute('aria-expanded', 'false');
    this.collectionSidebar.classList.remove(classes.expanded);

    if (collectionSidebarSlideOut) {
      this.accessibility.removeTrapFocus();
    }

    if (isScrollLocked) {
      document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true}));
    }
  }

  toggleSidebarSlider() {
    if (isMobile()) {
      this.hideSidebar();
    } else if (this.collectionSidebar.classList.contains(classes.expanded)) {
      this.showSidebarCallback();
    }
  }

  collectionSidebarClose(evt) {
    evt.preventDefault();
    this.hideSidebar();
    if (document.body.classList.contains(classes.focused) && this.groupTagsButton) {
      this.groupTagsButton.focus();
    }
  }

  groupTagsButtonClick() {
    const isScrollLocked = document.documentElement.hasAttribute('data-scroll-locked');

    if (isScrollLocked) {
      document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true}));
    }
  }

  onUnload() {
    if (this.groupTagsButton !== null) {
      document.removeEventListener('theme:resize:width', this.sidebarResizeEvent);
      this.groupTagsButton.removeEventListener('click', this.groupTagsButtonClickEvent);
    }

    if (this.collectionSidebarCloseButtons.length) {
      this.collectionSidebarCloseButtons.forEach((button) => {
        button.removeEventListener('click', this.collectionSidebarCloseEvent);
      });
    }

    if (this.sort) {
      this.sortLinks.forEach((link) => {
        link.removeEventListener('click', this.onSortButtonClickEvent);
      });
      this.sort.removeEventListener('theme:filter:sort', this.onSortCheckEvent);
    }
  }
}

const collectionSection = {
  onLoad() {
    sections[this.id] = new Collection(this);
  },
  onUnload() {
    sections[this.id].onUnload();
  },
};

register('collection', [slider, parallaxHero, collectionSection, tooltipSection]);

if (!customElements.get('collection-filters-form')) {
  customElements.define('collection-filters-form', CollectionFiltersForm);
}

if (!customElements.get('range-slider')) {
  customElements.define('range-slider', RangeSlider);
}
